import { useEffect, useState } from 'react';

import { APP_NAME, SUPPORT_EMAIL } from '../../config/branding';
import {
  APP_STORE_ID,
  APP_STORE_URL,
  LANDING_DESCRIPTION,
  LANDING_TITLE,
  PLAY_STORE_URL,
} from '../../modules/Landing/landingCopy';
import {
  WEB_DELETE_ACCOUNT_LEGACY_PATH,
  WEB_DELETE_ACCOUNT_PATH,
  WEB_PRIVACY_PATH,
  WEB_SUPPORT_PATH,
  WEB_TERMS_PATH,
  WEB_THANKS_PATH,
  isIndexablePath,
  isMarketingPath,
} from './webPaths';
import { getWebPathname, subscribeWebPathname } from './webRouteStore';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  if (typeof document === 'undefined') return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown> | null): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const script =
    existing instanceof HTMLScriptElement
      ? existing
      : document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}

function titleFor(path: string): string {
  if (path === WEB_PRIVACY_PATH) return `Privacy — ${APP_NAME}`;
  if (path === WEB_TERMS_PATH) return `Terms — ${APP_NAME}`;
  if (path === WEB_SUPPORT_PATH) return `Support — ${APP_NAME}`;
  if (path === WEB_THANKS_PATH) return `Thank you — ${APP_NAME}`;
  if (path === WEB_DELETE_ACCOUNT_PATH) return `Delete account — ${APP_NAME}`;
  if (isMarketingPath(path)) return `${LANDING_TITLE} — ${LANDING_DESCRIPTION}`;
  return APP_NAME;
}

function descriptionFor(path: string): string {
  if (path === WEB_PRIVACY_PATH) {
    return `${APP_NAME} privacy policy — how we handle account and financial data you upload, and the risks you accept.`;
  }
  if (path === WEB_TERMS_PATH) {
    return `${APP_NAME} terms of use — as-is service, no financial advice, and limits on liability for financial data.`;
  }
  if (path === WEB_SUPPORT_PATH) {
    return `Help with ${APP_NAME} — account, imports, billing, and household. Email ${SUPPORT_EMAIL}.`;
  }
  if (path === WEB_THANKS_PATH) {
    return `Thank you for subscribing to ${APP_NAME} Pro. Open the dashboard to use household, AI chat, and more analyses.`;
  }
  if (path === WEB_DELETE_ACCOUNT_PATH) {
    return `Request that FMA deactivate your ${APP_NAME} account. Steps, what we deactivate vs keep, and retention for a soft delete.`;
  }
  return LANDING_DESCRIPTION;
}

/**
 * Public routes are indexable, including `/app/delete-account` (Play deletion URL).
 * Other product routes under `/app` are noindex.
 */
export function WebDocumentMeta() {
  const [path, setPath] = useState(getWebPathname);

  useEffect(() => subscribeWebPathname(setPath), []);

  useEffect(() => {
    const indexable = isIndexablePath(path);
    document.title = titleFor(path);
    upsertMeta('name', 'description', descriptionFor(path));
    upsertMeta('name', 'apple-itunes-app', `app-id=${APP_STORE_ID}`);
    upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow');
    upsertMeta('property', 'og:title', titleFor(path));
    upsertMeta('property', 'og:description', descriptionFor(path));

    const origin = window.location.origin;
    if (indexable) {
      const normalized = path.replace(/\/+$/, '') || '/';
      const canonicalPath =
        normalized === WEB_DELETE_ACCOUNT_PATH || normalized === WEB_DELETE_ACCOUNT_LEGACY_PATH
          ? WEB_DELETE_ACCOUNT_PATH
          : normalized === '/'
            ? '/'
            : normalized;
      upsertCanonical(`${origin}${canonicalPath}`);
    }
    upsertJsonLd(
      'moneylens-jsonld',
      path.replace(/\/+$/, '') === '' || path === '/'
        ? {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: APP_NAME,
            applicationCategory: 'FinanceApplication',
            operatingSystem: 'iOS, Android, Web',
            url: origin,
            installUrl: [APP_STORE_URL, PLAY_STORE_URL],
            downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
            sameAs: [APP_STORE_URL, PLAY_STORE_URL],
            description: LANDING_DESCRIPTION,
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
          }
        : null,
    );
  }, [path]);

  return null;
}
